import axios from "axios";
import i18next from "i18next";
import { REQUEST_METHOD } from "common/enum/RequestMethod";
import { useEffect, useRef, useState } from "react";

const prodFrontEndPoint = process.env.REACT_APP_PROD_FRONT_URL;
const appEnv = process.env.REACT_APP_ENV;

let apiUrl = "";

if (appEnv === "production" && window?.location?.origin === prodFrontEndPoint) {
  apiUrl = process.env.REACT_APP_PROD_API_URL;
} else if (appEnv === "test") {
  apiUrl = process.env.REACT_APP_TEST_API_URL;
}

const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  config.headers["Accept-Language"] = i18next.language;

  const token = localStorage.getItem("signToken");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const token = localStorage.getItem("signToken");
    const noRedirect = error.config?.headers?.["No-Auth-Redirect"] === "true";

    if (error.response?.status === 401 && token && !noRedirect) {
      localStorage.clear();
      window.location.href = "/";
    }
    return Promise.reject(error);
  },
);

export default api;

export async function apiRequest({
  method = REQUEST_METHOD.GET,
  url,
  queryParameter = {},
  body = {},
  headers = {},
  noRedirect = false,
}) {
  const config = {
    method,
    url,
    headers: {
      ...headers,
      ...(noRedirect ? { "No-Auth-Redirect": "true" } : {}),
    },
    ...(method === REQUEST_METHOD.GET || method === REQUEST_METHOD.DELETE
      ? { params: queryParameter }
      : { data: body }),
  };

  return api(config);
}

export function useApiRequest({
  method = REQUEST_METHOD.GET,
  url,
  queryParameter = {},
  body = {},
  headers = {},
  immediate = true,
  onSuccess,
  onError,
  noRedirect = false,
}) {
  const [response, setResponse] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(immediate);
  const [trigger, setTrigger] = useState(0);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;

  const requestOverridesRef = useRef({
    overrideBody: body,
    overrideParams: queryParameter,
    overrideHeaders: headers,
  });

  const refetch = ({ overrideBody, overrideParams, overrideHeaders } = {}) => {
    requestOverridesRef.current = {
      overrideBody: overrideBody ?? requestOverridesRef.current.overrideBody,
      overrideParams:
        overrideParams ?? requestOverridesRef.current.overrideParams,
      overrideHeaders:
        overrideHeaders ?? requestOverridesRef.current.overrideHeaders,
    };
    setLoading(true);
    setTrigger((prev) => prev + 1);
  };

  useEffect(() => {
    if (!immediate && trigger === 0) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await apiRequest({
          method,
          url,
          body: requestOverridesRef.current.overrideBody,
          queryParameter: requestOverridesRef.current.overrideParams,
          headers: {
            ...requestOverridesRef.current.overrideHeaders,
            ...(noRedirect ? { "No-Auth-Redirect": "true" } : {}),
          },
        });

        setResponse(res);
        onSuccessRef.current?.(res.data);
      } catch (err) {
        setError(err);
        if (onErrorRef.current) {
          onErrorRef.current(err);
        } else {
          console.error(err);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [url, immediate, method, noRedirect, trigger]);

  return { response, data: response?.data, error, loading, refetch };
}
